import { createContext, useCallback, useContext, useMemo, useState } from 'react';

/** Máximo de imóveis comparáveis ao mesmo tempo (tabela legível em mobile). */
export const COMPARE_MAX = 3;

interface CompareContextValue {
  /** Ids selecionados, na ordem em que foram adicionados. */
  selectedIds: string[];
  /** Adiciona/remove o id. No limite, descarta o mais antigo (janela deslizante). */
  toggle: (propertyId: string) => void;
  /** Remove o id da seleção. */
  remove: (propertyId: string) => void;
  /** Esvazia a seleção. */
  clear: () => void;
  isSelected: (propertyId: string) => boolean;
  count: number;
  /** Ainda cabe pelo menos mais um imóvel na comparação. */
  canAddMore: boolean;
  max: number;
}

const noop = () => {};

const CompareContext = createContext<CompareContextValue>({
  selectedIds: [],
  toggle: noop,
  remove: noop,
  clear: noop,
  isSelected: () => false,
  count: 0,
  canAddMore: true,
  max: COMPARE_MAX,
});

/**
 * Seleção de imóveis para comparar — apenas em memória (NÃO persiste).
 * A seleção vive enquanto o app está aberto e é zerada ao entrar no modo
 * comparação em cada página, então o usuário sempre escolhe do zero ali.
 */
export function CompareProvider({ children }: { children: React.ReactNode }) {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const toggle = useCallback((propertyId: string) => {
    setSelectedIds((prev) => {
      if (prev.includes(propertyId)) return prev.filter((id) => id !== propertyId);
      if (prev.length >= COMPARE_MAX) return [...prev.slice(1), propertyId]; // descarta o mais antigo
      return [...prev, propertyId];
    });
  }, []);

  const remove = useCallback((propertyId: string) => {
    setSelectedIds((prev) => (prev.includes(propertyId) ? prev.filter((id) => id !== propertyId) : prev));
  }, []);

  const clear = useCallback(() => {
    setSelectedIds((prev) => (prev.length === 0 ? prev : []));
  }, []);

  const value = useMemo<CompareContextValue>(() => ({
    selectedIds,
    toggle,
    remove,
    clear,
    isSelected: (id: string) => selectedIds.includes(id),
    count: selectedIds.length,
    canAddMore: selectedIds.length < COMPARE_MAX,
    max: COMPARE_MAX,
  }), [selectedIds, toggle, remove, clear]);

  return <CompareContext.Provider value={value}>{children}</CompareContext.Provider>;
}

export function useCompare() {
  return useContext(CompareContext);
}
